const PRIVACY_SOURCE_RE = /^(?:<elided>|\[?redacted\]?|<hidden>|web\s*\(\s*privacy[- ]protected\s*\))$/i;

export const isPrivacyProtectedSourceValue = (value) => (
  PRIVACY_SOURCE_RE.test(String(value || '').trim())
);

export const sourceDisplayValue = (value) => (
  isPrivacyProtectedSourceValue(value)
    ? 'Privacy-protected source'
    : value
);

export const sourceTypeDisplayName = (sourceType, values = []) => {
  const type = String(sourceType || 'Unknown').trim() || 'Unknown';
  if (/privacy[- ]protected/i.test(type) || values.some(isPrivacyProtectedSourceValue)) {
    return /privacy[- ]protected/i.test(type) ? type : `${type} (privacy protected)`;
  }
  return type;
};

/**
 * What a data-source name says about it: its URI, web domain, whether it is outside the
 * organization (a public web host) and its kind (Web, Cloud Storage, S3, BigQuery, File, else Data
 * Store). A label, never a count. Agent 360 reads its sources' kinds through it too.
 */
export const sourceMetadata = (label = '') => {
  const uri = String(label || '');
  const lower = uri.toLowerCase();
  let domain = '';
  let isExternal = false;

  if (/^https?:\/\//i.test(uri)) {
    try {
      const host = new URL(uri).hostname;
      domain = host;
      isExternal = !(
        host === 'localhost'
        || host.endsWith('.local')
        || host.endsWith('.internal')
        || /^10\./.test(host)
        || /^192\.168\./.test(host)
        || /^172\.(1[6-9]|2\d|3[01])\./.test(host)
      );
    } catch {
      // Keep an unparseable source as an internal datastore name.
    }
  }

  let sourceType = 'Data Store';
  if (/^https?:\/\//i.test(uri)) sourceType = 'Web';
  else if (lower.startsWith('gs://')) sourceType = 'Cloud Storage';
  else if (lower.startsWith('s3://')) sourceType = 'S3';
  else if (lower.startsWith('bq://') || lower.includes('bigquery')) sourceType = 'BigQuery';
  else if (lower.startsWith('file://')) sourceType = 'File';

  return { uri, domain, isExternal, sourceType };
};

// A reference with no address is named `inline:<file>`; a data store made from an uploaded file has the
// id `file:<name>`, so its domain reads `datastore:file:<name>`. A GCS or other data store (`datastore:gcs_…`)
// is not a file.
const FILE_DOMAIN_RE = /^(?:inline:|datastore:file:)/i;
const FILE_EXTENSION_RE = /\.([a-z0-9]{1,5})$/i;

/**
 * Whether a source's domain value is really a file. Returns { name, extension } (the name without
 * the marker), or null for a website's host, a bucket or a data store.
 */
export const sourceFileOf = (domain) => {
  const value = String(domain || '').trim();
  if (!FILE_DOMAIN_RE.test(value)) return null;
  const name = value.replace(FILE_DOMAIN_RE, '');
  return { name, extension: (name.match(FILE_EXTENSION_RE) || [])[1] || '' };
};

/** The domain column's text: a file's name, else the host, bucket or data store as it is. */
export const sourceDomainOrFile = (domain) => sourceFileOf(domain)?.name || sourceDisplayValue(domain);

/**
 * The extension of the file a source is, or '' when it is not one: from its domain when that names a
 * file, else from its title when that is a file name (no address, and an extension we draw an icon for,
 * so "Acme.com" or "v1.2" is not taken for a file).
 */
export const sourceFileExtension = (source, known = () => true) => {
  const fromDomain = sourceFileOf(source?.domain)?.extension;
  if (fromDomain) return fromDomain;
  const title = String(source?.title || '').trim();
  if (/[\\/:]/.test(title)) return '';
  const ext = (title.match(FILE_EXTENSION_RE) || [])[1] || '';
  return known(ext) ? ext : '';
};
